import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import type { LoginMode, LoginOption } from './auth-types';
import { getWSServerUrl, isAdmissionGuard } from '@/lib/environment';
import { getApiErrorMessage } from '@/lib/http';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function reasonMessage(reason: string | null) {
  switch (reason) {
    case 'sso_link_expired':
      return 'Your saved sign-in link is no longer valid — Single Sign-On links now rotate automatically for security. Enter your email to sign in.';
    case 'sso_link_invalid':
      return 'That sign-in link is malformed or missing. Enter your email to try again.';
    case 'sso_config_error':
      return 'Your organization’s Single Sign-On is not fully configured yet. Contact your administrator, or enter your email to see other sign-in options.';
    case 'sso_error':
      return 'We hit an unexpected error starting your Single Sign-On flow. Please try again.';
    case 'session_auth_mismatch':
    case 'session_org_mismatch':
    case 'session_scope_violation':
      return 'Your session no longer matches the selected workspace. Sign in again to continue.';
    case 'requires_sso':
      return 'This organization requires Single Sign-On. Enter your email and choose the SSO option.';
    case 'session_expired':
      return 'Your session expired. Sign in again to continue.';
    default:
      return null;
  }
}

function EmailIcon() {
  return (
    <svg className="ats-login-email-icon" focusable="false" aria-hidden="true" viewBox="0 0 24 24">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 4-8 5-8-5V6l8 5 8-5z" />
    </svg>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user,
    ready,
    loading: authLoading,
    resolveLoginOptions,
    sendOtp,
    verifyOtp,
    logout,
  } = useAuth();

  //const redirectUrl = searchParams.get('redirect') || '/';
  const redirectUrl = '/';
  const reason = reasonMessage(searchParams.get('reason'));
  const inspectMode = searchParams.get('mode') === 'inspect';

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(reason);
  const [loginMode, setLoginMode] = useState<LoginMode>('unknown');
  const [options, setOptions] = useState<LoginOption[]>([]);
  const [ssoToken, setSsoToken] = useState<string | null>(null);
  const [ssoOrgName, setSsoOrgName] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const productCopy = isAdmissionGuard()
    ? 'Guard your admission today'
    : 'Hiring Just Got Easier — Thanks to Lantern AI';

  const emailError = useMemo(() => {
    if (!email) return '';
    return emailRegex.test(email) ? '' : 'You must enter a valid email.';
  }, [email]);

  useEffect(() => {
    if (countdown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setCountdown((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown]);

  async function startOtpFlow() {
    setIsSendingOtp(true);
    try {
      await sendOtp(email, inspectMode);
      setIsOtpSent(true);
      setCountdown(60);
      setError('');
    } catch (sendError) {
      setError(getApiErrorMessage(sendError, 'Failed to send verification code.'));
    } finally {
      setIsSendingOtp(false);
    }
  }

  async function resolveAndContinue() {
    if (!email || emailError) {
      setError(emailError || 'Email is required.');
      return;
    }

    setIsResolving(true);
    setError('');

    try {
      const loginOptions = await resolveLoginOptions(email);

      if (loginOptions.length > 1) {
        setOptions(loginOptions);
        setLoginMode('picker');
        return;
      }

      const only = loginOptions[0];
      if (only?.kind === 'sso') {
        setSsoToken(only.ssoToken);
        setSsoOrgName(only.organizationName || '');
        setLoginMode('sso');
        return;
      }

      setLoginMode('otp');
      await startOtpFlow();
    } catch (resolveError) {
      setError(getApiErrorMessage(resolveError, 'Failed to resolve login method.'));
    } finally {
      setIsResolving(false);
    }
  }

  async function pickOption(option: LoginOption) {
    setError('');

    if (option.kind === 'sso') {
      setSsoToken(option.ssoToken);
      setSsoOrgName(option.organizationName || '');
      setLoginMode('sso');
      return;
    }

    setLoginMode('otp');
    await startOtpFlow();
  }

  function continueWithSso(forceLogin: boolean) {
    if (!ssoToken) return;

    const query = forceLogin ? '?forceLogin=1' : '';
    window.location.href = `${getWSServerUrl()}/api/auth/workos/authorize/${encodeURIComponent(ssoToken)}${query}`;
  }

  function resetToEmailStep() {
    setLoginMode('unknown');
    setOptions([]);
    setSsoToken(null);
    setSsoOrgName('');
    setOtp('');
    setError('');
    setIsOtpSent(false);
    setCountdown(0);
  }

  async function handleVerifyOtp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!email || !otp) {
      setError('Email and verification code are required.');
      return;
    }

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit code.');
      return;
    }

    setIsVerifyingOtp(true);
    setError('');

    try {
      await verifyOtp(email, otp);
      navigate(redirectUrl, { replace: true });
    } catch (verifyError) {
      setError(getApiErrorMessage(verifyError, 'Invalid code. Please try again.'));
    } finally {
      setIsVerifyingOtp(false);
    }
  }

  async function handleLogout() {
    const idpLogoutUrl = await logout();
    if (idpLogoutUrl) {
      window.location.href = idpLogoutUrl;
    }
  }

  return (
    <div className="login-page">
      <div className="login-shell">
        <img
          src={
            isAdmissionGuard()
              ? 'https://d2cgute8qahbr9.cloudfront.net/ag/ag_logo_lantern_v2.svg'
              : 'https://d2cgute8qahbr9.cloudfront.net/lantern/lantern_logo.svg'
          }
          alt={isAdmissionGuard() ? 'AG logo' : 'Lantern logo'}
          className="login-logo"
        />

        <div className="login-card">
          <span className="ats-login-copyright">© 2026 Lantern AI, Inc. All rights reserved.</span>

          <h1 className="ats-login-title">Welcome Back</h1>
          <h1 className="ats-login-subtitle">{productCopy}</h1>
          <div className="ats-login-divider" />

          {!authLoading && ready && user?.email ? (
            <div className="signed-in-strip">
              Signed in as <strong>{user.email}</strong>. <Link to={redirectUrl}>Continue to app</Link> or{' '}
              <button type="button" onClick={handleLogout}>
                log out
              </button>
              .
            </div>
          ) : null}

          {notice ? (
            <div className="login-notice">
              <span>{notice}</span>
              <button type="button" aria-label="Dismiss notice" onClick={() => setNotice(null)}>
                ×
              </button>
            </div>
          ) : null}

          {loginMode === 'picker' ? (
            <div className="login-step">
              <h2>How would you like to sign in?</h2>
              <p className="muted">
                Signing in as <strong>{email}</strong>.
              </p>
              <div className="login-option-list">
                {options.map((option, index) => (
                  <button
                    key={option.kind === 'sso' ? `${option.organizationId}-${index}` : `otp-${index}`}
                    className={option.kind === 'sso' ? 'login-option sso' : 'login-option otp'}
                    type="button"
                    onClick={() => void pickOption(option)}
                  >
                    <span>
                      <strong>
                        {option.kind === 'sso'
                          ? `Sign in to ${option.organizationName || 'your organization'}`
                          : 'Sign in with verification code'}
                      </strong>
                      <small>
                        {option.kind === 'sso' ? 'via Single Sign-On' : 'For your other organizations'}
                      </small>
                    </span>
                  </button>
                ))}
              </div>
              <button type="button" className="text-button" onClick={resetToEmailStep}>
                Back
              </button>
            </div>
          ) : loginMode === 'sso' ? (
            <div className="login-step">
              <h2>{ssoOrgName ? `Sign in to ${ssoOrgName}` : 'Your organization uses Single Sign-On'}</h2>
              <p className="muted">
                Signing in as <strong>{email}</strong>. We will redirect you to your identity provider to complete
                login.
              </p>
              <button
                type="button"
                className="primary-button primary-button-filled"
                onClick={() => continueWithSso(false)}
              >
                Continue with Single Sign-On
              </button>
              <button type="button" className="text-button" onClick={resetToEmailStep}>
                Back
              </button>
            </div>
          ) : (
            <form className="login-step" onSubmit={handleVerifyOtp}>
              <div className="field-label">Email</div>
              <div className={emailError ? 'input-shell error' : 'input-shell'}>
                <input
                  id="email"
                  type="email"
                  value={email}
                  disabled={isOtpSent}
                  required
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setNotice(null);
                    if (loginMode !== 'unknown') resetToEmailStep();
                  }}
                  autoComplete="email"
                />
                <button className="email-icon-button" type="button">
                  <EmailIcon />
                </button>
              </div>
              <p className={emailError ? 'field-error ats-login-helper' : 'ats-login-helper'}>{emailError || ' '}</p>

              {!isOtpSent ? (
                <button
                  className="primary-button"
                  type="button"
                  disabled={isResolving || !email || Boolean(emailError)}
                  onClick={() => void resolveAndContinue()}
                >
                  {isResolving ? 'Checking…' : 'Continue'}
                </button>
              ) : (
                <>
                  <div className="field-label otp-field-label">Verification Code</div>
                  <div className="input-shell otp-input">
                    <input
                      id="otp"
                      inputMode="numeric"
                      value={otp}
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="Enter 6-digit code"
                      required
                      onChange={(event) => {
                        setOtp(event.target.value.replace(/\D/g, '').slice(0, 6));
                      }}
                    />
                  </div>
                  <p className="muted">
                    We sent a 6-digit verification code to {email}. If you do not receive it, please check your spam
                    or junk folder.
                  </p>
                  <div className="login-row">
                    <button type="button" className="text-button" onClick={resetToEmailStep}>
                      Change email
                    </button>
                    <button
                      type="button"
                      className="text-button"
                      disabled={countdown > 0 || isSendingOtp}
                      onClick={() => void startOtpFlow()}
                    >
                      {isSendingOtp ? 'Sending…' : countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
                    </button>
                  </div>
                  <button
                    className="primary-button primary-button-filled"
                    type="submit"
                    disabled={isVerifyingOtp || otp.length !== 6}
                  >
                    {isVerifyingOtp ? 'Verifying…' : 'Login'}
                  </button>
                </>
              )}
            </form>
          )}

          {error ? <p className="login-error">{error}</p> : null}
        </div>
      </div>
    </div>
  );
}
